import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserAuthHeaders } from '../utils/auth';
import { PLANBASE_CONFIG } from '../config';
import { cn } from '../lib/utils';
import {
  AcademicCapIcon,
  ArrowRightIcon,
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  BuildingLibraryIcon,
  CheckIcon,
  FolderIcon,
  HomeModernIcon,
  RectangleGroupIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

/**
 * Post-signup onboarding — three quick steps that turn a fresh account
 * into a personalized landing. Wired into the router as `/welcome` and
 * navigated to from SignupPage + AuthCallback on their first sign-in.
 *
 * Step 1 — practice focus (pill grid of six architecture programs).
 * Step 2 — role (three cards: Student / Practitioner / Firm).
 * Step 3 — where to start (three cards routing to Orbi / Portfolio
 * Setup / Discover).
 *
 * Every step accepts Skip. On finish, writes the answers into
 * `user.metadata.onboarding` via the members/me PATCH so subsequent
 * sessions land straight on the target page instead of re-onboarding.
 */

type PracticeFocus =
  | 'housing'
  | 'commercial'
  | 'cultural'
  | 'hospitality'
  | 'educational'
  | 'mixed-use';

type Role = 'student' | 'practitioner' | 'firm';
type Landing = 'orbi' | 'portfolio' | 'discover';

const PRACTICE_OPTIONS: { id: PracticeFocus; label: string; icon: typeof HomeModernIcon }[] = [
  { id: 'housing', label: 'Housing', icon: HomeModernIcon },
  { id: 'commercial', label: 'Commercial', icon: BuildingOfficeIcon },
  { id: 'cultural', label: 'Cultural', icon: BuildingLibraryIcon },
  { id: 'hospitality', label: 'Hospitality', icon: BuildingOffice2Icon },
  { id: 'educational', label: 'Educational', icon: AcademicCapIcon },
  { id: 'mixed-use', label: 'Mixed-use', icon: RectangleGroupIcon },
];

const ROLE_OPTIONS: { id: Role; label: string; hint: string; icon: typeof AcademicCapIcon }[] = [
  { id: 'student', label: 'Student', hint: 'Studying architecture', icon: AcademicCapIcon },
  { id: 'practitioner', label: 'Practitioner', hint: 'Working as an architect or designer', icon: UserGroupIcon },
  { id: 'firm', label: 'Firm', hint: 'Running or part of a studio', icon: BuildingOffice2Icon },
];

const LANDING_OPTIONS: { id: Landing; label: string; hint: string; icon: typeof SparklesIcon; to: string }[] = [
  { id: 'orbi', label: 'Draft with Orbi', hint: 'Start a workflow or ask a research question', icon: SparklesIcon, to: '/orbi' },
  { id: 'portfolio', label: 'Set up my portfolio', hint: 'Claim a URL and add your first project', icon: FolderIcon, to: '/portfolio-setup' },
  { id: 'discover', label: 'Browse Discover', hint: 'See what others are publishing', icon: RectangleGroupIcon, to: '/discover' },
];

export default function Welcome() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [practice, setPractice] = useState<PracticeFocus | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [saving, setSaving] = useState(false);

  // Existing users get sent straight to Discover — this guard means
  // AuthCallback and SignupPage can all navigate to /welcome without
  // having to duplicate the "have they onboarded yet?" check.
  useEffect(() => {
    if (user?.metadata?.onboarding?.completedAt) {
      navigate('/discover', { replace: true });
    }
  }, [user, navigate]);

  const finish = async (landing: Landing) => {
    setSaving(true);
    // Persist so we don't onboard the user again. Failure is non-blocking —
    // route the user forward regardless; they'll just see the onboarding
    // again on next sign-in.
    try {
      await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/members/me`, {
        method: 'PATCH',
        headers: getUserAuthHeaders(),
        body: JSON.stringify({
          metadata: {
            ...(user?.metadata || {}),
            onboarding: {
              practice,
              role,
              landing,
              completedAt: new Date().toISOString(),
            },
          },
        }),
      });
      refreshUser?.();
    } catch {
      /* non-blocking */
    }
    const target = LANDING_OPTIONS.find((o) => o.id === landing)?.to || '/discover';
    navigate(target, { replace: true });
  };

  const displayName = user?.name || user?.email?.split('@')[0] || 'there';

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col px-6 py-10 md:py-16">
        {/* Progress rail */}
        <div className="mb-10 flex items-center gap-2" role="progressbar" aria-valuemin={1} aria-valuemax={3} aria-valuenow={step}>
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className={cn(
                'h-1 flex-1 rounded-full transition-colors',
                n <= step ? 'bg-primary' : 'bg-muted',
              )}
            />
          ))}
        </div>

        {step === 1 && (
          <section aria-labelledby="wc-step-1" className="flex flex-1 flex-col">
            <div className="mb-8">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-primary">Step 1 of 3</p>
              <h1 id="wc-step-1" className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
                Welcome, {displayName}.<br />What kind of work do you do most?
              </h1>
              <p className="mt-3 text-sm text-muted-foreground">
                We'll seed your Orbi starter prompts around this. You can change it later.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {PRACTICE_OPTIONS.map((opt) => {
                const OptIcon = opt.icon;
                const active = practice === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setPractice(opt.id)}
                    aria-pressed={active}
                    className={cn(
                      'flex flex-col items-start gap-3 rounded-2xl border px-4 py-4 text-left transition-colors',
                      active
                        ? 'border-primary bg-primary/5'
                        : 'border-border bg-card hover:border-primary/60',
                    )}
                  >
                    <OptIcon className={cn('h-5 w-5', active ? 'text-primary' : 'text-muted-foreground')} />
                    <span className={cn('text-sm font-medium', active ? 'text-primary' : 'text-foreground')}>
                      {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-auto flex items-center justify-between pt-10">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!practice}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Continue <ArrowRightIcon className="h-4 w-4" />
              </button>
            </div>
          </section>
        )}

        {step === 2 && (
          <section aria-labelledby="wc-step-2" className="flex flex-1 flex-col">
            <div className="mb-8">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-primary">Step 2 of 3</p>
              <h1 id="wc-step-2" className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
                And how would you describe yourself?
              </h1>
              <p className="mt-3 text-sm text-muted-foreground">
                Shapes tone and defaults across the app — nothing shown publicly.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              {ROLE_OPTIONS.map((opt) => {
                const OptIcon = opt.icon;
                const active = role === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setRole(opt.id)}
                    aria-pressed={active}
                    className={cn(
                      'flex items-start gap-4 rounded-2xl border px-5 py-4 text-left transition-colors',
                      active
                        ? 'border-primary bg-primary/5'
                        : 'border-border bg-card hover:border-primary/60',
                    )}
                  >
                    <OptIcon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', active ? 'text-primary' : 'text-muted-foreground')} />
                    <div className="flex-1">
                      <div className={cn('text-sm font-medium', active ? 'text-primary' : 'text-foreground')}>
                        {opt.label}
                      </div>
                      <div className="text-xs text-muted-foreground">{opt.hint}</div>
                    </div>
                    {active && <CheckIcon className="mt-1 h-4 w-4 flex-shrink-0 text-primary" />}
                  </button>
                );
              })}
            </div>

            <div className="mt-auto flex items-center justify-between pt-10">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Back
              </button>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="text-sm text-muted-foreground hover:text-foreground"
                >
                  Skip
                </button>
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  disabled={!role}
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Continue <ArrowRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </section>
        )}

        {step === 3 && (
          <section aria-labelledby="wc-step-3" className="flex flex-1 flex-col">
            <div className="mb-8">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-primary">Step 3 of 3</p>
              <h1 id="wc-step-3" className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
                Where do you want to start?
              </h1>
              <p className="mt-3 text-sm text-muted-foreground">
                Pick one — you can move between all of them anytime from the sidebar.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              {LANDING_OPTIONS.map((opt) => {
                const OptIcon = opt.icon;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => finish(opt.id)}
                    disabled={saving}
                    className="group flex items-start gap-4 rounded-2xl border border-border bg-card px-5 py-4 text-left transition-colors hover:border-primary/60 disabled:opacity-60"
                  >
                    <OptIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground group-hover:text-primary" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-foreground">{opt.label}</div>
                      <div className="text-xs text-muted-foreground">{opt.hint}</div>
                    </div>
                    <ArrowRightIcon className="mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground group-hover:text-primary" />
                  </button>
                );
              })}
            </div>

            <div className="mt-auto flex items-center justify-between pt-10">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Back
              </button>
              <button
                type="button"
                onClick={() => finish('discover')}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Skip and browse Discover
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
